<template>
    <div
        v-if="widget"
        :class="[
            limitStatusType ? `is--status-${limitStatusType}` : '',
            layoutClass,
            { 'no-animation': isVisualAlertDisabled }
        ]"
        class="widget-counter-card h-full py-2 px-3"
    >
        <div class="w-full h-full flex items-center">
            <div
                class="w-full"
            >
                <RedesignBasePopover
                    :disabled="!$slots['content-popover']"
                    :trigger="popoverTrigger"
                    :value="popoverOpen"
                    @input="emit('update:popoverOpen', $event)"
                >
                    <template #reference>
                        <div
                            v-if="layoutClass === 'layout-two-rows-title'"
                            :class="alignClass"
                            class="widget-counter-card__content"
                        >
                            <div
                                :class="alignContentClass"
                                class="flex items-center gap-x-2 gap-y-1 flex-wrap w-auto"
                            >
                                <div class="widget-counter-card__icon">
                                    <slot name="icon">
                                        <WidgetStatusIcon
                                            :icon="icon"
                                            :color="iconColor"
                                            :size="finalIconSize"
                                            class="widget-counter-card__icon-icon"
                                        />
                                    </slot>
                                </div>
                                <div
                                    v-if="showText"
                                    :style="{
                                        fontSize: `${layoutConfig.titleFontSize}px`,
                                    }"
                                    class="widget-counter-card__title"
                                >
                                    <slot name="content-title">
                                        {{ cardText }}
                                    </slot>
                                </div>
                            </div>
                            <div
                                :class="alignContentClass"
                                class="flex items-center w-auto"
                            >
                                <div
                                    :style="{
                                        fontSize: `${layoutConfig.valueFontSize}px`,
                                    }"
                                    class="widget-counter-card__value"
                                >
                                    <slot name="content-value">
                                        {{ cardValue }}
                                    </slot>
                                </div>
                            </div>
                        </div>
                        <div
                            v-else-if="layoutClass === 'layout-two-rows-value'"
                            :class="alignClass"
                            class="widget-counter-card__content"
                        >
                            <div
                                :class="alignContentClass"
                                class="flex items-center w-auto"
                            >
                                <div class="widget-counter-card__icon">
                                    <slot name="icon">
                                        <WidgetStatusIcon
                                            :icon="icon"
                                            :color="iconColor"
                                            :size="finalIconSize"
                                            class="widget-counter-card__icon-icon"
                                        />
                                    </slot>
                                </div>
                            </div>
                            <div
                                :class="alignContentClass"
                                class="flex items-center gap-x-2 gap-y-1 flex-wrap w-auto"
                            >
                                <div
                                    v-if="showText"
                                    :style="{
                                        fontSize: `${layoutConfig.titleFontSize}px`,
                                    }"
                                    class="widget-counter-card__title"
                                >
                                    <slot name="content-title">
                                        {{ cardText }}
                                    </slot>
                                </div>
                                <div
                                    :style="{
                                        fontSize: `${layoutConfig.valueFontSize}px`,
                                    }"
                                    class="widget-counter-card__value"
                                >
                                    <slot name="content-value">
                                        {{ cardValue }}
                                    </slot>
                                </div>
                            </div>
                        </div>
                        <div
                            v-else
                            :class="alignClass"
                            class="widget-counter-card__content"
                        >
                            <div class="widget-counter-card__icon">
                                <slot name="icon">
                                    <WidgetStatusIcon
                                        :icon="icon"
                                        :color="iconColor"
                                        :size="finalIconSize"
                                        class="widget-counter-card__icon-icon"
                                    />
                                </slot>
                            </div>
                            <div
                                v-if="showText"
                                :style="{
                                    fontSize: `${layoutConfig.titleFontSize}px`,
                                }"
                                class="widget-counter-card__title"
                            >
                                <slot name="content-title">
                                    {{ cardText }}
                                </slot>
                            </div>

                            <div
                                :style="{
                                    fontSize: `${layoutConfig.valueFontSize}px`,
                                }"
                                class="widget-counter-card__value"
                            >
                                <slot name="content-value">
                                    {{ cardValue }}
                                </slot>
                            </div>
                        </div>
                    </template>
                    <slot name="content-popover" />
                </RedesignBasePopover>
            </div>
        </div>
        <RedesignBaseSpinner
            v-if="loading"
            class="absolute right-1 bottom-0"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WidgetExtended } from '@/types/dashboard'
import WidgetStatusIcon from '@/components/RedesignComponents/Cards/WidgetStatusIcon.vue'
import {
    COUNTER_CARD_LAYOUT_DEFAULT,
    COUNTER_CARD_LAYOUT_DEFAULT_VALUE,
    COUNTER_CARD_LAYOUT_PREFIX,
    WIDGET_CONTENT_ALIGNMENT
} from '@/enum/default-layout-config'
import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import { isVisualAlertDisabled } from '@/composables/globalData/useLayouts'

const DEFAULT_TITLE_FONT_SIZE = 20
const DEFAULT_FONT_SIZE = 32
const DEFAULT_VALUE_ICON_SIZE = 32
const ICON_OFFSET = 12

const props = withDefaults(
    defineProps<{
        widget: WidgetExtended
        showText?: boolean
        displayBorder?: boolean
        cardText?: string
        cardValue?: string | number
        icon?: string
        iconColor?: string
        iconSize?: number
        selectedType?: number
        animation?: boolean
        loading?: boolean
        limitStatusType?: string
        popoverTrigger?: string
        popoverOpen?: boolean
    }>(),
    {
        showText: true,
        displayBorder: true,
        cardText: '',
        cardValue: '',
        icon: '',
        iconColor: '',
        iconSize: 0,
        selectedType: 0,
        animation: false,
        loading: false,
        limitStatusType: '',
        popoverTrigger: 'click',
        popoverOpen: false
    }
)

const emit = defineEmits<{
    (e: 'update:popoverOpen', value: boolean): void
}>()

/* Computed */
const widgetLayout = computed(() => {
    return props.widget.WidgetLayout ?? {}
})
const alignClass = computed(() => {
    const align = widgetLayout.value.contentAlignment || WIDGET_CONTENT_ALIGNMENT
    return layoutClass.value === COUNTER_CARD_LAYOUT_DEFAULT ? `text-${align} justify-${align}` : `text-${align} items-${align}`
})
const alignContentClass = computed(() => {
    const align = widgetLayout.value.contentAlignment || WIDGET_CONTENT_ALIGNMENT
    return `justify-${align}`
})
const layoutConfig = computed(() => {
    return {
        titleFontSize: widgetLayout.value.titleFontSize || DEFAULT_TITLE_FONT_SIZE,
        valueFontSize: widgetLayout.value.valueFontSize || DEFAULT_FONT_SIZE,
        valueIconMinWidth: widgetLayout.value.valueIconMinWidth || DEFAULT_VALUE_ICON_SIZE
    }
})
const layoutClass = computed(() => {
    const _layoutTemplateValue = widgetLayout.value.contentLayout || COUNTER_CARD_LAYOUT_DEFAULT_VALUE
    return `${COUNTER_CARD_LAYOUT_PREFIX}-${_layoutTemplateValue}`
})
const finalIconSize = computed(() => {
    const baseSize = props.iconSize || layoutConfig.value.valueIconMinWidth
    const _finalSize = baseSize - ICON_OFFSET
    return Math.max(_finalSize, 0)
})
</script>

<style lang="scss">
.widget-counter-card {
    @apply w-full h-full transition-all duration-300;
    &__content {
        @apply flex items-center gap-x-2 gap-y-1;
    }

    &__icon {
        @apply p-1.5 inline-flex items-center justify-center overflow-hidden;
        &-icon {
            @apply rounded;
        }
    }

    &__title, &__value {
        @apply font-bold uppercase;
        text-wrap: balance;
    }

    &__value {
        font-variant-numeric: tabular-nums;
    }

    &.layout-default {
        .widget-counter-card__content {
            @apply flex-row flex-wrap;
        }
    }

    /* Layouts */
    &.layout-three-rows {
        .widget-counter-card__content {
            @apply flex-col gap-1;
        }
    }

    &.layout-two-rows-title,
    &.layout-two-rows-value {
        .widget-counter-card__content {
            @apply flex-col gap-1;
        }
    }
}
</style>
